import { SHARE_ID } from '#lib/shareId.ts'
import lzstring from 'lz-string'
import { legacyProjectShape, withoutBuildOutputs, type Project } from '#lib/Project.svelte.ts'
import { serializer } from '#lib/json.ts'
import { toast } from '#stores/toastStore.ts'

export function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value))
}

export function minBigInt(a: bigint, b: bigint): bigint {
    return a < b ? a : b
}
export function maxBigInt(a: bigint, b: bigint): bigint {
    return a > b ? a : b
}
export function clampBigInt(value: bigint, min: bigint, max: bigint): bigint {
    return maxBigInt(min, minBigInt(value, max))
}

export function unsignedBigIntToSigned(unsignedBigInt: bigint, numBytes: number) {
    const bitLength = numBytes * 8
    const signBitMask = 1n << (BigInt(bitLength) - 1n)

    if ((unsignedBigInt & signBitMask) !== 0n) {
        const twoComplementMask = (1n << BigInt(bitLength)) - 1n
        return unsignedBigInt - (twoComplementMask + 1n)
    }

    return unsignedBigInt
}

export function blobDownloader(blob: Blob, fileName: string) {
    const a = document.createElement('a')
    a.style.display = 'none'
    document.body.appendChild(a)
    a.href = URL.createObjectURL(blob)
    a.download = fileName
    a.click()
    a.remove()
    URL.revokeObjectURL(a.href)
}
/**
 * Saves bytes under exactly `fileName`. The Blob is typed as bytes: Chrome names an untyped
 * download without an extension as text, `crackme.txt` for `crackme`.
 */
export function bytesDownloader(bytes: Uint8Array, fileName: string) {
    const contents = new Uint8Array(bytes).buffer
    blobDownloader(new Blob([contents], { type: 'application/octet-stream' }), fileName)
}
export function textDownloader(text: string, fileName: string) {
    blobDownloader(new Blob([text], { type: 'text/json' }), fileName)
}

/**
 * Copies `text` to the clipboard and says how that went in a toast, `label` naming what it is, as
 * "Share link". Answers whether it was copied, for a caller whose text is nowhere else on the page:
 * a browser can refuse, when the click that asked is too long ago or the page has lost focus, and a
 * page served without HTTPS has no clipboard at all.
 */
export async function copyText(text: string, label: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text)
        toast.logPill(`${label} copied to clipboard`)
        return true
    } catch (error) {
        console.error(error)
        toast.error(
            `Couldn't copy the ${label[0].toLowerCase()}${label.slice(1)} to the clipboard.`
        )
        return false
    }
}

export function delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms))
}
export function formatTime(s: number) {
    //format in seconds, milliseconds
    const seconds = Math.floor(s / 1000)
    const milliseconds = Math.floor(s % 1000)
    if (seconds === 0) {
        return `${milliseconds}ms`
    }
    return `${seconds}.${`${milliseconds}`.padStart(3, '0')}s`
}

export function capitalize(word: string) {
    return word[0].toUpperCase() + word.slice(1)
}

/**
 * The compressed `?project=` payload a share link carries.
 *
 * Split out from createShareLink so in-app navigation can put it behind a resolve()d
 * route instead of an absolute origin-prefixed URL, without duplicating the encoding.
 */
/**
 * What a `?project=` payload may grow to. A share link has to survive being pasted into a chat
 * message, an email or a browser address bar, and those stop well short of what a Project's Files
 * can reach: a single 1 MiB binary asset compresses to roughly 370,000 characters. Past this, the
 * honest answer is an `.asmproj` archive, not a link that silently fails wherever it is pasted.
 */
export const SHARE_PAYLOAD_LIMIT = 32_000

/**
 * The most a Project's Files may hold, in characters of content, and still be compressed to see
 * whether it fits a link. The payload's 32,000 characters carry 6 bits each, 24,000 bytes, so a
 * Project past this would need its JSON compressed more than 40 times, which lz-string reaches
 * only on long runs of one character: compressing such a Project took seconds before it said no,
 * a 64 MiB one half a minute and then a RangeError. It is refused at once instead.
 */
export const SHARE_CONTENT_LIMIT = 1024 * 1024

/**
 * How much of a Project's JSON is compressed first, to refuse before compressing the rest: a
 * prefix's compression is the start of the whole's, but for its last few characters, so when the
 * prefix alone passes the limit the whole does too.
 */
const SHARE_PROBE_LENGTH = 128 * 1024

export class ShareTooLargeError extends Error {
    constructor() {
        super('This Project is too large to share as a link')
        this.name = 'ShareTooLargeError'
    }
}

/**
 * A Project with one Program is shared in the shape before Programs, which an older app the service
 * worker still serves can open, as archives are; only several Programs or none need the new one
 * ([programs-plan.md](../../../../docs/design/programs-plan.md), decision 4). The Build outputs
 * nobody changed are left out, which the person who opens it builds again
 * ([ADR 0056](../../../../docs/adr/0056-a-build-saves-the-program-it-links-as-a-file.md)).
 */
export function createSharePayload(project: Project): string {
    const snapshot = withoutBuildOutputs(project.toObject())
    let content = 0
    for (const file of Object.values(snapshot.files)) content += file.content.length
    if (content > SHARE_CONTENT_LIMIT) throw new ShareTooLargeError()
    const p = legacyProjectShape(snapshot) ?? snapshot
    p.id = SHARE_ID
    const json = serializer.stringify(p)
    //lz-string's last code and its end mark, which a prefix's compression ends with, take a few
    //characters; a prefix past the limit by more than them leaves the whole past it
    if (
        json.length > SHARE_PROBE_LENGTH &&
        lzstring.compressToEncodedURIComponent(json.slice(0, SHARE_PROBE_LENGTH)).length >
            SHARE_PAYLOAD_LIMIT + 16
    )
        throw new ShareTooLargeError()
    const payload = lzstring.compressToEncodedURIComponent(json)
    if (payload.length > SHARE_PAYLOAD_LIMIT) throw new ShareTooLargeError()
    return payload
}

export function createShareLink(project: Project): string {
    const code = createSharePayload(project)
    return `${window.location.origin}/projects/share?project=${code}`
}

function hexDigest(buffer: ArrayBuffer) {
    let digest = ''
    const view = new DataView(buffer)
    for (let i = 0; i < view.byteLength; i += 4) {
        const value = view.getUint32(i)
        const stringValue = value.toString(16)
        const padding = '00000000'
        const paddedValue = (padding + stringValue).slice(-padding.length)
        digest += paddedValue
    }

    return digest
}

export async function makeHash(data: string) {
    const encoder = new TextEncoder()
    const password = encoder.encode(data)
    const hash = await window.crypto.subtle.digest('SHA-256', password)
    return hexDigest(hash)
}

export async function encryptData(data: string, key: string): Promise<string> {
    const password = (await makeHash(key)).slice(0, 128 / 8)
    const encoder = new TextEncoder()
    const iv = window.crypto.getRandomValues(new Uint8Array(12)) // AES-GCM requires a 12-byte IV
    const keyBuffer = await window.crypto.subtle.importKey(
        'raw',
        encoder.encode(password),
        { name: 'AES-GCM' },
        false,
        ['encrypt']
    )
    const encrypted = await window.crypto.subtle.encrypt(
        {
            name: 'AES-GCM',
            iv: iv
        },
        keyBuffer,
        encoder.encode(data)
    )
    const encryptedArray = new Uint8Array(encrypted)
    const encryptedBase64 = btoa(String.fromCharCode(...encryptedArray))
    const ivBase64 = btoa(String.fromCharCode(...iv))
    return `${ivBase64}:${encryptedBase64}`
}

export async function decryptData(encryptedData: string, key: string): Promise<string> {
    const password = (await makeHash(key)).slice(0, 128 / 8)
    const encoder = new TextEncoder()
    const [ivBase64, encryptedBase64] = encryptedData.split(':')
    const iv = Uint8Array.from(atob(ivBase64), (c) => c.charCodeAt(0))
    const encryptedArray = Uint8Array.from(atob(encryptedBase64), (c) => c.charCodeAt(0))
    const keyBuffer = await window.crypto.subtle.importKey(
        'raw',
        encoder.encode(password),
        { name: 'AES-GCM' },
        false,
        ['decrypt']
    )
    const decrypted = await window.crypto.subtle.decrypt(
        {
            name: 'AES-GCM',
            iv: iv
        },
        keyBuffer,
        encryptedArray
    )
    const decoder = new TextDecoder()
    return decoder.decode(decrypted)
}
